import React, { useState } from 'react';

import styled from '@emotion/styled';

import { Alert } from '@shared/components/bootstrap';

import { SingleSelectFormControl } from '@admin/components/fields/single_select_form_control';
import { client } from '@admin/libraries/apollo';
import { usePalletSkuReassignMutation, useLogisticsAccountSkusQuery, LogisticsPalletInfoDocument } from '@admin/schema';

const AlertContainer = styled.div`
  margin-bottom: 8px;
`;

interface EditPalletSkuAndLoadProps {
  palletId: string;
  currentSkuId: string;
  accountId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const useEditPalletSkuAndLoad = (
  palletId: string,
  currentSkuId: string,
  accountId: string,
  onSuccess: () => void,
) => {
  const [selectedNewSkuId, setSelectedNewSkuId] = useState<string>(currentSkuId);
  const [message, setMessage] = useState<{ type: 'danger' | 'success'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const [reassignMutation] = usePalletSkuReassignMutation({
    client,
    refetchQueries: [
      {
        query: LogisticsPalletInfoDocument,
        variables: { id: palletId },
      },
    ],
  });

  const accountSkusQuery = useLogisticsAccountSkusQuery({
    client,
    variables: { accountId },
    skip: !accountId,
  });

  const accountSkus = accountSkusQuery.data?.logisticsSkus || [];
  const skuOptions = accountSkus.map((sku) => ({
    value: sku.id,
    label: `${sku.value} ${sku.description ? `(${sku.description})` : ''}`,
  }));

  const handleSubmit = async () => {
    if (!selectedNewSkuId) return;

    setLoading(true);
    setMessage(null);

    try {
      const result = await reassignMutation({
        variables: {
          input: {
            palletId,
            currentSkuId,
            newSkuId: selectedNewSkuId,
          },
        },
      });

      if (result.data?.palletSkuReassign?.status === 'OK') {
        setMessage({ type: 'success', text: 'SKU updated successfully!' });
        onSuccess();
      } else {
        setMessage({
          type: 'danger',
          text: result.data?.palletSkuReassign?.error || 'Failed to update',
        });
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedNewSkuId,
    setSelectedNewSkuId,
    message,
    setMessage,
    loading,
    setLoading,
    skuOptions,
    accountSkusQuery,
    handleSubmit,
  };
};
